import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzTableModule } from 'ng-zorro-antd/table';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { Router } from '@angular/router';
import { Person } from '../../api/person-api.class';
import { StoreService } from '../../store/store.service';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { firstValueFrom, Observable } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { PersonApiService } from '../../api/person-api.service';
import { NzSpinComponent } from 'ng-zorro-antd/spin';

@Component({
  selector: 'app-persons-list',
  standalone: true,
  imports: [
    NzButtonComponent,
    NzIconModule,
    NzAutocompleteModule,
    NgForOf,
    NzTableModule,
    FormsModule,
    NzInputModule,
    AsyncPipe,
    NgIf,
    NzSpinComponent,
  ],
  templateUrl: './persons-list.component.html',
  styleUrl: './persons-list.component.scss',
})
export class PersonsListComponent implements AfterViewInit {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  loading = false;
  selectedPerson: number | undefined;
  searchValue = '';
  persons$: Observable<Person[]>;

  listOfSortFilterOptions = {
    sortById: (a: Person, b: Person) => a.id - b.id,
    sortByFirstName: (a: Person, b: Person) =>
      a.vorname.localeCompare(b.vorname),
    sortByLastName: (a: Person, b: Person) => a.name.localeCompare(b.name),
  };

  constructor(
    private store: StoreService,
    private router: Router,
    private message: NzMessageService,
    private personApi: PersonApiService,
  ) {
    this.persons$ = this.store.select((state) => state.persons.filteredPersons);
  }

  ngAfterViewInit() {
    this.searchInput.nativeElement.focus();
  }

  async onSearch(event: Event): Promise<void> {
    const raw = this.searchValue.trim();
    if (!raw || raw.length < 2) {
      if (
        event.type === 'click' ||
        (event instanceof KeyboardEvent && event.key === 'Enter')
      ) {
        this.message.warning(
          'Bitte geben Sie mehr als ein Zeichen zum Suchen an.',
        );
      }
      return;
    }

    this.loading = true;

    const terms = raw.split(/\s+/).filter(Boolean);
    const results = new Map<number, Person>(); // Deduplizieren per ID

    try {
      // Für jeden Suchbegriff separat eine Anfrage stellen
      for (const term of terms) {
        try {
          const [byName, byGroup] = await Promise.all([
            firstValueFrom(this.personApi.getAllPersons({ name: term })),
            firstValueFrom(this.personApi.getAllPersons({ gruppe: term })),
          ]);

          [...byName, ...byGroup].forEach((p) =>
            results.set(p.id, new Person(p)),
          );
          // const persons = await firstValueFrom(
          //   this.personApi.getAllPersons({ name: term }),
          // );
          // persons.forEach((p) => results.set(p.id, new Person(p)));
        } catch (err) {
          console.warn(`Keine Treffer für ${term}`);
        }
      }

      // Alle eindeutigen Personen aus der Map holen
      const combinedResults = Array.from(results.values());

      // Clientseitig nach allen Suchbegriffen filtern
      const filtered = combinedResults.filter((person) =>
        this.matchesAllTerms(person, terms),
      );

      // Gefilterte Personen im Store setzen
      const {
        persons: { getFilteredPersons, setFilteredPersonsClient },
      } = this.store.getActions();

      await setFilteredPersonsClient(filtered);
      getFilteredPersons();
    } finally {
      this.loading = false;
    }
  }

  onPersonClick(data: Person): void {
    this.selectedPerson = data.id;
    this.router.navigate(['persons', this.selectedPerson]);
  }

  private matchesAllTerms(person: Person, terms: string[]): boolean {
    const searchable =
      `${person.vorname} ${person.name} ${person.gruppe}`.toLowerCase();

    return terms.every((term) => searchable.includes(term.toLowerCase()));
  }
}
